import React, { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Constants for better maintainability
const HASH_DELAY = 100;
const SCROLL_DELAY = 100;
const SCROLL_BEHAVIOR = 'smooth' as ScrollBehavior;

// Scrollable container selectors in order of priority
const SCROLLABLE_SELECTORS = [
  '.page-content',
  '.main-content', 
  '.App',
  '.floating-page-card'
] as const;

const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  
  // Cache DOM references to avoid repeated queries
  const cachedElementsRef = useRef<Map<string, Element | null>>(new Map());

  // Memoized function to get cached or query DOM elements
  const getCachedElement = useCallback((selector: string): Element | null => {
    const cache = cachedElementsRef.current;
    
    if (cache.has(selector)) {
      return cache.get(selector) ?? null;
    }
    
    const element = document.querySelector(selector);
    cache.set(selector, element);
    return element;
  }, []);

  // Optimized scroll to top function
  const scrollToTop = useCallback(() => {
    try {
      // Primary method - immediate scroll (no behavior = instant)
      window.scrollTo(0, 0);
      
      // Fallback methods for different browsers
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      
      // Handle scrollable containers
      SCROLLABLE_SELECTORS.forEach(selector => {
        const element = getCachedElement(selector);
        if (element) {
          (element as HTMLElement).scrollTop = 0;
          if ((element as HTMLElement).scrollTo) {
            (element as HTMLElement).scrollTo(0, 0);
          }
        }
      });
    } catch (error) {
      console.error('ScrollToTop: Error scrolling to top:', error);
    }
  }, [getCachedElement]);

  // Memoized hash scroll function
  const scrollToHash = useCallback((hashValue: string) => {
    try {
      const element = document.querySelector(hashValue);
      if (element) {
        element.scrollIntoView({ behavior: SCROLL_BEHAVIOR });
      } else {
        console.warn(`ScrollToTop: Hash element "${hashValue}" not found`);
      }
    } catch (error) {
      console.error('ScrollToTop: Error scrolling to hash:', error);
    }
  }, []);

  useEffect(() => {
    // Handle hash navigation
    if (hash) {
      const timeoutId = setTimeout(() => {
        scrollToHash(hash);
      }, HASH_DELAY);
      
      return () => clearTimeout(timeoutId);
    }

    // Handle regular route changes
    scrollToTop();
    
    // Additional scrolls for mobile compatibility - multiple attempts
    const timeoutId1 = setTimeout(scrollToTop, SCROLL_DELAY);
    const timeoutId2 = setTimeout(scrollToTop, SCROLL_DELAY * 2);
    const timeoutId3 = setTimeout(scrollToTop, SCROLL_DELAY * 3);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [pathname, hash, scrollToHash, scrollToTop]);

  return null;
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(ScrollToTop); 